/**
 * Maintenance history list for one site — every issue ever reported against a
 * structure, open ones first, with the resolution record beside each closed
 * one and a Resolve action for those still open.
 *
 * Reads through the shared ServerDataGrid in its in-memory mode: the view
 * loads the whole log once, so sorting and filtering stay client-side. That
 * also puts the list on the shared row-height contract, which is what lets a
 * phone row grow into a card instead of being clipped at one desktop row.
 *
 * Contains:
 * - MaintenanceHistoryGrid    — the list (default export)
 * - MaintenanceHistoryListItem — mobile card for one entry
 */
import { Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import GridMobileCard from '@sophocles/data-grids/components/mobile/GridMobileCard';
import ServerDataGrid from '@sophocles/data-grids/components/ServerDataGrid';
import { formatUserString } from '@sophocles/utils';
import { selectMaintenanceHistoryCardMeta } from '@sophocles/views/sites/components/maintenanceHistoryCard';

// What a column prints where the entry has nothing to say. Cards omit the line
// instead, so the rows themselves carry null and this stays presentational.
const NO_VALUE = '—';

const formatDay = value => (value ? value.format('L') : '');

function resolveStatusCell(row, t) {
  return row.resolved ? (
    <GridStatusCell color="ok" label={t('general.resolved', 'Resolved')} />
  ) : (
    <GridStatusCell color="attention" label={t('general.unresolved', 'Unresolved')} />
  );
}

function renderResolveAction(row, t, onResolve) {
  return (
    <Tooltip title={t('actions.markResolved', 'Mark resolved')}>
      <Button size="small" variant="outlined" onClick={() => onResolve(row._log)}>
        {t('actions.resolve', 'Resolve')}
      </Button>
    </Tooltip>
  );
}

/**
 * @param {object}   props
 * @param {object}   props.row       - mapped grid row
 * @param {boolean}  props.canResolve - the entry is open and the user may close it
 * @param {Function} props.onResolve  - fn(log) — opens the resolve dialog
 */
function MaintenanceHistoryListItem({ row, canResolve, onResolve }) {
  const { t } = useTranslation();

  return (
    <GridMobileCard
      primary={row.issue}
      meta={selectMaintenanceHistoryCardMeta(row, { t, formatDate: formatDay })}
      status={resolveStatusCell(row, t)}
      actions={canResolve ? renderResolveAction(row, t, onResolve) : null}
      // The list navigates nowhere — only the Resolve action is interactive.
      sx={{ cursor: 'default' }}
    />
  );
}

function MaintenanceHistoryGrid({ data, loading, canEditEntries, onResolve }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const apiRef = useGridApiRef();

  const rows = useMemo(() => {
    const resolvedRows = data
      .filterNot(item => item.get('date_resolved', null) == null)
      .sortBy(item => item.get('time_entered'))
      .sortBy(item => item.get('date_resolved'))
      .reverse();
    const unresolvedRows = data
      .filter(item => item.get('date_resolved', null) == null)
      .sortBy(item => item.get('time_entered'))
      .sortBy(item => item.get('date_reported'))
      .reverse();

    return unresolvedRows
      .concat(resolvedRows)
      .toArray()
      .map(log => ({
        id: log.get('id', 0),
        _log: log,
        reported: log.get('date_reported', null),
        reportedBy: formatUserString(log.get('entered_by', null)) || null,
        issue: log.get('issue', ''),
        resolved: log.get('date_resolved', null),
        resolvedBy: formatUserString(log.get('resolved_by', null)) || null,
        technician: log.get('resolved_technician', null) || null,
        resolution: log.get('resolution', null) || null,
      }));
  }, [data]);

  const desktopColumns = useMemo(
    () => [
      {
        field: 'status',
        headerName: t('general.status'),
        width: 130,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => resolveStatusCell(params.row, t),
      },
      {
        field: 'reported',
        headerName: t('userSettings.reported', 'Reported'),
        width: 120,
        valueFormatter: formatDay,
      },
      {
        field: 'reportedBy',
        headerName: t('userSettings.reportedBy', 'Reported By'),
        width: 160,
        valueFormatter: value => value || NO_VALUE,
      },
      {
        field: 'issue',
        headerName: t('general.description'),
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'resolved',
        headerName: t('general.resolved', 'Resolved'),
        width: 120,
        valueFormatter: value => (value ? value.format('L') : NO_VALUE),
      },
      {
        field: 'resolvedBy',
        headerName: t('userSettings.resolvedBy', 'Resolved By'),
        width: 160,
        valueFormatter: value => value || NO_VALUE,
      },
      {
        field: 'technician',
        headerName: t('userSettings.technician', 'Technician'),
        width: 160,
        valueFormatter: value => value || NO_VALUE,
      },
      {
        field: 'resolution',
        headerName: t('userSettings.resolution', 'Resolution'),
        flex: 1,
        minWidth: 200,
        valueFormatter: value => value || NO_VALUE,
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params =>
          !params.row.resolved && canEditEntries ? renderResolveAction(params.row, t, onResolve) : null,
      },
    ],
    [t, canEditEntries, onResolve],
  );

  // Mobile collapses to one full-width column whose cell is the card; the grid
  // hides the header chrome and lets the row measure itself.
  const columns = useMemo(() => {
    if (!isMobile) return desktopColumns;
    return [
      {
        field: 'summary',
        headerName: '',
        flex: 1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <MaintenanceHistoryListItem row={row} canResolve={!row.resolved && canEditEntries} onResolve={onResolve} />
        ),
      },
    ];
  }, [isMobile, desktopColumns, canEditEntries, onResolve]);

  const getRowId = useCallback(row => row.id, []);
  const gridState = useMemo(() => ({ apiRef, loading, getRowId }), [apiRef, loading, getRowId]);

  return (
    <ServerDataGrid
      gridState={gridState}
      columns={columns}
      rows={rows}
      showSearch={false}
      showGridSettings={false}
      zebra={false}
      density="compact"
      localeText={{ noRowsLabel: t('general.noResultsMessage') }}
    />
  );
}

export default MaintenanceHistoryGrid;
